// Living Partyhattar on a player's board: each one adds a sip to every hit their cards
// land. Counted off the board on every hit, like Happy Hour's discount, so a synced
// state cannot claim a hat it does not have.
function partyBonus(player) {
  return (player?.minions || []).filter((minion) => minion.ability === "partyhat" && minion.hp > 0).length;
}

// Everyone at the table, the player first and then round the table as the lists come:
// partners, then opponents. Both lists hold only the living.
function wholeTable(self, allies = [], enemies = []) {
  return [self, ...allies, ...enemies];
}

export function createCardLibrary({
  addSips,
  attackPlayer: plainAttack,
  discardCard,
  drawCard,
  hitMinions,
  millCards,
  randomInt,
  removeSips,
  stealCard,
  summonMinion,
  swapHands,
  // The mini-game cards hand the table over to a game everyone plays on their phone.
  // Defaulted, because most callers build the library to test cards that never start one.
  startMinigame = () => {},
}) {
  // Every card hits through this, so a Partyhatt boosts card damage and nothing else —
  // a minion's ping, a cactus prick or a mirror bounce come from inside combat and stay
  // their own size. A card that hits for nothing (Hämnden with nothing to avenge) stays
  // nothing rather than becoming a one-sip hat trick.
  const attackPlayer = (source, target, amount) => plainAttack(source, target, amount > 0 ? amount + partyBonus(source) : amount);

  const library = [
  {
    id: "zero-lager",
    name: "Nollprocentig Lager",
    cost: 1,
    tag: "Skum",
    image: "beer",
    text: "Målet tar 3 klunkar.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => attackPlayer(self, target, 3),
  },
  {
    id: "lime-mocktail",
    name: "Lime-Mocktail",
    cost: 3,
    tag: "Friskt",
    image: "cocktail",
    text: "Målet tar 4 klunkar. Om du har minst 18 klunkar tar du bort 2.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      attackPlayer(self, target, 4);
      if (self.sipsTaken >= 18) removeSips(self, 2);
    },
  },
  {
    id: "bubbelvatten",
    name: "Bubbelvatten",
    cost: 4,
    tag: "Splash",
    image: "bubbles",
    text: "Alla andra tar 2 klunkar.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => enemies.forEach((enemy) => attackPlayer(self, enemy, 2)),
  },
  {
    id: "virgin-colada",
    name: "Virgin Colada",
    cost: 3,
    tag: "Heal",
    image: "pineapple",
    text: "Ta bort 4 klunkar från dig själv och dra ett kort.",
    effect: "heal",
    target: "self",
    play: ({ self }) => {
      removeSips(self, 4);
      drawCard(self);
    },
  },
  {
    id: "gurkvatten",
    name: "Gurkvatten",
    cost: 2,
    tag: "Minion",
    image: "cucumber",
    text: "Placera Gurkvakten, en försvarare med 3 HP.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Gurkvakten", 3),
  },
  {
    id: "espresso-tonic",
    name: "Espresso Tonic",
    cost: 3,
    tag: "Tempo",
    image: "coffee",
    text: "Målet tar 3 klunkar och får -1 mana nästa tur.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      attackPlayer(self, target, 3);
      // Added, not set: an Espresso must not wipe out what Kreditkortet borrowed.
      target.manaPenalty += 1;
    },
  },
  {
    id: "jordgubbs-fizz",
    name: "Jordgubbs-Fizz",
    cost: 5,
    tag: "Burst",
    image: "berry",
    text: "Målet tar 6 klunkar.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => attackPlayer(self, target, 6),
  },
  {
    id: "isvatten",
    name: "Isvattenpaus",
    cost: 0,
    tag: "Gratis",
    image: "ice",
    text: "Ta bort 1 klunk och dra ett kort.",
    effect: "heal",
    target: "self",
    play: ({ self }) => {
      removeSips(self, 1);
      drawCard(self);
    },
  },
  {
    id: "ingefara",
    name: "Ingefärs-Shot",
    cost: 5,
    tag: "Pierce",
    image: "shot",
    text: "Målet tar 5 klunkar direkt, förbi minions.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => addSips(target, 5, self),
  },
  {
    id: "kombucha",
    name: "Kombucha-Knuff",
    cost: 2,
    tag: "Random",
    image: "kombucha",
    text: "Målet tar mellan 1 och 5 klunkar.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => attackPlayer(self, target, randomInt(1, 5)),
  },
  {
    id: "citronpress",
    name: "Citronpress",
    cost: 1,
    tag: "Syra",
    image: "lemon",
    text: "Gör 2 klunkar. Om du dödar en minion, dra ett kort.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      const before = target.minions.length;
      attackPlayer(self, target, 2);
      if (target.minions.length < before) drawCard(self);
    },
  },
  {
    id: "sista-droppen",
    name: "Sista Droppen",
    cost: 6,
    tag: "Final",
    image: "drop",
    text: "Målet tar 7 klunkar. Om målet når 24 klunkar tar de 2 till.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      attackPlayer(self, target, 7);
      if (target.sipsTaken >= 24 && target.sipsTaken < target.maxSips) addSips(target, 2, self);
    },
  },
  {
    id: "barhandduk",
    name: "Barhandduk",
    cost: 4,
    tag: "Minion",
    image: "ice",
    text: "Placera en Barhandduk med 5 HP som fångar upp klunkar.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Barhandduk", 5),
  },
  {
    id: "spegelglas",
    name: "Spegelglas",
    cost: 4,
    tag: "Reflect",
    image: "kombucha",
    text: "Nästa gång du tar klunkdamage tar angriparen lika många.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      self.reflect = true;
    },
  },
  {
    id: "kapsyl",
    name: "Kapsylkastaren",
    cost: 1,
    tag: "Minion",
    image: "kapsyl",
    text: "Placera Kapsylkastaren (2 HP). Vid ditt turslut: 1 klunk till en slumpad motståndare.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Kapsylkastaren", 2, "pinger"),
  },
  {
    id: "kaktus",
    name: "Kaktusvakten",
    cost: 5,
    tag: "Minion",
    image: "kaktus",
    text: "Placera Kaktusvakten (6 HP). Den som attackerar den tar 1 klunk — spikar!",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Kaktusvakten", 6, "thorns"),
  },
  {
    id: "happyhour",
    name: "Happy Hour-skylten",
    cost: 3,
    tag: "Minion",
    image: "happyhour",
    text: "Placera Happy Hour-skylten (3 HP). Dina kort kostar 1 mindre så länge den lever.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Happy Hour-skylten", 3, "discount"),
  },
  {
    id: "partytalt",
    name: "Partytält",
    cost: 6,
    tag: "Minion",
    image: "talt",
    text: "Res Partytältet, en enorm försvarare med 8 HP.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Partytältet", 8),
  },
  // ── Teamwork ────────────────────────────────────────────────────────────────
  // Cards that need a partner. The 2 mot 2 standard deck deals them (TEAM_CARD_IDS in
  // deckRules.js), and the deckbuilder offers them under their own tab — a built deck
  // is played in every mode, so outside a 2 mot 2 they only do their solo half. Each
  // one reads `allies` — the *living* teammates the controller passes in — and does
  // that solo half, or nothing, when the list is empty: a partner can be knocked out
  // mid-turn, and the bot's drift test plays every card with no ally at all.
  {
    id: "lagskal",
    name: "Lagskålen",
    cost: 3,
    tag: "Lagarbete",
    image: "skal",
    text: "Du och din partner tar bort 3 klunkar var.",
    effect: "heal",
    target: "self",
    teamOnly: true,
    play: ({ self, allies = [] }) => {
      removeSips(self, 3);
      allies.forEach((mate) => removeSips(mate, 3));
    },
  },
  {
    id: "ryggstod",
    name: "Ryggstöd",
    cost: 3,
    tag: "Lagarbete",
    image: "ryggstod",
    text: "Din partner får en Livvakt (3 HP) som tar träffarna.",
    effect: "shield",
    target: "self",
    teamOnly: true,
    play: ({ allies = [] }) => {
      allies.forEach((mate) => summonMinion(mate, "Livvakt", 3));
    },
  },
  {
    id: "passning",
    name: "Passningen",
    cost: 2,
    tag: "Lagarbete",
    image: "passning",
    text: "Din partner drar 2 kort.",
    effect: "trick",
    target: "self",
    teamOnly: true,
    play: ({ allies = [] }) => {
      allies.forEach((mate) => {
        drawCard(mate);
        drawCard(mate);
      });
    },
  },
  {
    id: "dubbelstot",
    name: "Dubbelstöten",
    cost: 4,
    tag: "Lagarbete",
    image: "dubbelstot",
    text: "Målet tar 4 klunkar, plus 3 till om din partner är kvar.",
    effect: "damage",
    target: "enemy",
    teamOnly: true,
    play: ({ self, target, allies = [] }) => attackPlayer(self, target, allies.length ? 7 : 4),
  },
  {
    id: "uppoffring",
    name: "Uppoffringen",
    cost: 2,
    tag: "Lagarbete",
    image: "uppoffring",
    text: "Din partner tar bort 5 klunkar. Du tar 2.",
    effect: "heal",
    target: "self",
    teamOnly: true,
    play: ({ self, allies = [] }) => {
      if (!allies.length) return;
      allies.forEach((mate) => removeSips(mate, 5));
      addSips(self, 2);
    },
  },
  // Everything below is reached only through deckbuilding — the standard deck in
  // deckRules.js is frozen at the eighteen cards at the top.
  {
    id: "skal",
    name: "Skål!",
    cost: 2,
    tag: "Skål",
    image: "klirr",
    text: "Alla andra tar 2 klunkar — och du tar 2.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      enemies.forEach((enemy) => attackPlayer(self, enemy, 2));
      // Straight down, past your own minions: you raise your own glass.
      addSips(self, 2, self);
    },
  },
  {
    id: "dubbelshot",
    name: "Dubbelshot",
    cost: 3,
    tag: "Multi",
    image: "dubbelshot",
    text: "Målet tar 2 klunkar, två gånger.",
    effect: "damage",
    target: "enemy",
    // Two separate hits: an umbrella stops only the first, a cactus pricks twice.
    play: ({ self, target }) => {
      attackPlayer(self, target, 2);
      attackPlayer(self, target, 2);
    },
  },
  {
    id: "shotroulette",
    name: "Shotroulette",
    cost: 3,
    tag: "Chans",
    image: "roulette",
    text: "En slumpad spelare — kanske du — tar 6 klunkar.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      const table = [self, ...enemies];
      const victim = table[randomInt(0, table.length - 1)];
      // Losing your own roulette goes straight down: your minions do not drink for you.
      if (victim === self) addSips(self, 6, self);
      else attackPlayer(self, victim, 6);
    },
  },
  {
    id: "ficktjuven",
    name: "Ficktjuven",
    cost: 2,
    tag: "Stöld",
    image: "ficktjuv",
    text: "Stjäl ett slumpat kort från målets hand.",
    effect: "trick",
    target: "enemy",
    play: ({ self, target }) => stealCard(target, self),
  },
  {
    id: "patar",
    name: "Påtår",
    cost: 2,
    tag: "Dra",
    image: "patar",
    text: "Dra två kort.",
    effect: "trick",
    target: "self",
    play: ({ self }) => {
      drawCard(self);
      drawCard(self);
    },
  },
  {
    id: "bytt-ar-bytt",
    name: "Bytt är bytt",
    cost: 3,
    tag: "Byte",
    image: "byte",
    text: "Byt hela handen med målet.",
    effect: "trick",
    target: "enemy",
    play: ({ self, target }) => swapHands(self, target),
  },
  {
    id: "dricks",
    name: "Dricks",
    cost: 0,
    tag: "Rabatt",
    image: "dricks",
    text: "Ditt nästa kort kostar 2 mindre.",
    effect: "trick",
    target: "self",
    play: ({ self }) => {
      self.discount = 2;
    },
  },
  {
    id: "kreditkortet",
    name: "Kreditkortet",
    cost: 0,
    tag: "Lån",
    image: "kredit",
    text: "Få 2 mana nu. Nästa tur får du 2 mindre.",
    effect: "trick",
    target: "self",
    play: ({ self }) => {
      self.mana += 2;
      self.manaPenalty += 2;
    },
  },
  {
    id: "dela-notan",
    name: "Dela notan",
    cost: 6,
    tag: "Utjämning",
    image: "nota",
    text: "Du och målet hamnar båda på snittet av era klunkar, avrundat uppåt.",
    effect: "trick",
    target: "enemy",
    play: ({ self, target }) => {
      const even = Math.ceil((self.sipsTaken + target.sipsTaken) / 2);
      const selfChange = even - self.sipsTaken;
      const targetChange = even - target.sipsTaken;
      // Sips come off before they go on. Nobody can end above the higher of the two
      // counts, so the bill never knocks anyone out — but a mirror can bounce it.
      if (selfChange < 0) removeSips(self, -selfChange);
      if (targetChange < 0) removeSips(target, -targetChange);
      if (selfChange > 0) addSips(self, selfChange, self);
      if (targetChange > 0) addSips(target, targetChange, self);
    },
  },
  {
    id: "vattenkaraffen",
    name: "Vattenkaraffen",
    cost: 1,
    tag: "Heal",
    image: "karaff",
    text: "Alla spelare tar bort 2 klunkar.",
    effect: "heal",
    target: "self",
    play: ({ self, allies = [], enemies }) => {
      removeSips(self, 2);
      // Both lists hold only the living, so the jug cannot raise anyone who is out — and
      // in a 2 mot 2 the partner is an ally, not an enemy, so it has to be poured for too.
      [...allies, ...enemies].forEach((player) => removeSips(player, 2));
    },
  },
  {
    id: "bakfyllan",
    name: "Bakfyllan",
    cost: 4,
    tag: "Fördröjd",
    image: "bakfylla",
    text: "Målet tar 2 klunkar nu och 3 till i början av sin nästa tur, förbi minions.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      // An umbrella that catches the drink also saves the morning after.
      if (!attackPlayer(self, target, 2)) return;
      target.hangover += 3;
      target.hangoverBy = self.id;
    },
  },
  {
    id: "paraplydrinken",
    name: "Paraplydrinken",
    cost: 2,
    tag: "Skydd",
    image: "paraply",
    text: "Nästa attack mot dig missar helt.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      self.dodge = true;
    },
  },
  {
    id: "bartendern",
    name: "Bartendern",
    cost: 4,
    tag: "Minion",
    image: "bartender",
    text: "Placera Bartendern (3 HP). Vid ditt turslut tar du bort 1 klunk.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Bartendern", 3, "bartender"),
  },
  {
    id: "smallkaramellen",
    name: "Smällkaramellen",
    cost: 2,
    tag: "Minion",
    image: "karamell",
    text: "Placera Smällkaramellen (2 HP). Den som slår ut den tar 3 klunkar.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Smällkaramellen", 2, "popper"),
  },
  {
    id: "hembryggaren",
    name: "Hembryggaren",
    cost: 3,
    tag: "Minion",
    image: "brygg",
    text: "Placera Hembryggaren (2 HP). Den växer med 1 HP vid ditt turslut, upp till 6.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Hembryggaren", 2, "brew"),
  },
  {
    id: "pubrundan",
    name: "Pubrundan",
    cost: 3,
    tag: "Runda",
    image: "pubrunda",
    text: "Alla andra tar 1 klunk per minion du har, minst 1.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      const round = Math.max(1, self.minions.filter((minion) => minion.hp > 0).length);
      enemies.forEach((enemy) => attackPlayer(self, enemy, round));
    },
  },
  {
    id: "konfettikanonen",
    name: "Konfettikanonen",
    cost: 3,
    tag: "Hand",
    image: "konfetti",
    text: "Målet tar lika många klunkar som du har kort kvar på handen.",
    effect: "damage",
    target: "enemy",
    // The cannon has already left the hand when this runs, so it does not count itself.
    play: ({ self, target }) => attackPlayer(self, target, self.hand.length),
  },
  {
    id: "glaskrossen",
    name: "Glaskrossen",
    cost: 5,
    tag: "Kross",
    image: "glaskross",
    text: "Slå ut alla målets minions.",
    effect: "damage",
    target: "enemy",
    // One hit exactly the size of the whole wall: it chews through every minion — each
    // one pricking or popping on the way — and nothing spills onto the player.
    play: ({ self, target }) => attackPlayer(self, target, target.minions.reduce((total, minion) => total + Math.max(0, minion.hp), 0)),
  },
  {
    id: "utslagsvasken",
    name: "Utslagsvasken",
    cost: 2,
    tag: "Kortlek",
    image: "vask",
    text: "Målet slänger de 3 översta korten i sin kortlek.",
    effect: "trick",
    target: "enemy",
    play: ({ target }) => millCards(target, 3),
  },
  {
    id: "stangningsdags",
    name: "Stängningsdags",
    cost: 4,
    tag: "Comeback",
    image: "stangning",
    text: "Har du minst 20 klunkar tar alla andra 4. Annars tar de 1.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      const amount = self.sipsTaken >= 20 ? 4 : 1;
      enemies.forEach((enemy) => attackPlayer(self, enemy, amount));
    },
  },
  {
    id: "snyltgasten",
    name: "Snyltgästen",
    cost: 2,
    tag: "Parasit",
    image: "snylt",
    text: "Sätt Snyltgästen (1 HP) hos målet. Den får målet att dricka 1 klunk i början av varje tur tills den slås ut.",
    effect: "trick",
    target: "enemy",
    // It sits on the target's board, so the next attack on them knocks it out first.
    play: ({ self, target }) => summonMinion(target, "Snyltgästen", 1, "leech", self),
  },
  // ── The second deckbuilding wave ────────────────────────────────────────────
  {
    id: "lattol",
    name: "Lättöl",
    cost: 1,
    tag: "Lätt",
    image: "lattol",
    text: "Målet tar 2 klunkar. Har målet druckit mest vid bordet drar du ett kort.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target, allies, enemies }) => {
      attackPlayer(self, target, 2);
      const others = wholeTable(self, allies, enemies).filter((player) => player !== target);
      if (others.every((player) => target.sipsTaken >= player.sipsTaken)) drawCard(self);
    },
  },
  {
    id: "sugroret",
    name: "Sugröret",
    cost: 1,
    tag: "Multi",
    image: "sugror",
    text: "Målet tar 1 klunk, tre gånger.",
    effect: "damage",
    target: "enemy",
    // Three separate hits: each one takes down a 1 HP minion on its own.
    play: ({ self, target }) => {
      for (let sip = 0; sip < 3; sip += 1) attackPlayer(self, target, 1);
    },
  },
  {
    id: "kanonbollen",
    name: "Kanonbollen",
    cost: 7,
    tag: "Burst",
    image: "kanon",
    text: "Målet tar 9 klunkar.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => attackPlayer(self, target, 9),
  },
  {
    id: "sockerchocken",
    name: "Sockerchocken",
    cost: 2,
    tag: "Kombo",
    image: "socker",
    text: "Målet tar 2 klunkar. Har du redan spelat ett kort den här turen tar det 2 till.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      attackPlayer(self, target, 2);
      if (self.playedCount > 0) attackPlayer(self, target, 2);
    },
  },
  {
    id: "langdrinken",
    name: "Långdrinken",
    cost: 4,
    tag: "Fördröjd",
    image: "langdrink",
    text: "Målet tar 2 klunkar nu och 1 i början av var och en av sina tre nästa turer.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      if (!attackPlayer(self, target, 2)) return;
      target.drip += 3;
      target.dripBy = self.id;
    },
  },
  {
    id: "hamnden",
    name: "Hämnden",
    cost: 3,
    tag: "Hämnd",
    image: "hamnd",
    text: "Målet tar lika många klunkar som du har druckit sedan din förra tur.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => attackPlayer(self, target, self.recentSips || 0),
  },
  {
    id: "champagneduschen",
    name: "Champagneduschen",
    cost: 6,
    tag: "Splash",
    image: "champagne",
    text: "Alla andra tar 3 klunkar.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => enemies.forEach((enemy) => attackPlayer(self, enemy, 3)),
  },
  {
    id: "rundan-pa-mig",
    name: "Rundan på mig",
    cost: 4,
    tag: "Runda",
    image: "runda",
    text: "Alla andra tar 3 klunkar — och du tar 3.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      enemies.forEach((enemy) => attackPlayer(self, enemy, 3));
      // Like Skål!: your own glass goes straight down, past your own minions.
      addSips(self, 3, self);
    },
  },
  {
    id: "spillet",
    name: "Spillet",
    cost: 2,
    tag: "Chans",
    image: "spill",
    text: "Två slumpade motståndare tar 2 klunkar var.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      const pool = [...enemies];
      for (let splash = 0; splash < 2 && pool.length; splash += 1) {
        const [victim] = pool.splice(randomInt(0, pool.length - 1), 1);
        attackPlayer(self, victim, 2);
      }
    },
  },
  {
    id: "bordsdansen",
    name: "Bordsdansen",
    cost: 4,
    tag: "Dans",
    image: "bordsdans",
    text: "Alla tar 2 klunkar, du också. Dra 2 kort.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      enemies.forEach((enemy) => attackPlayer(self, enemy, 2));
      addSips(self, 2, self);
      drawCard(self);
      drawCard(self);
    },
  },
  {
    id: "skumpartyt",
    name: "Skumpartyt",
    cost: 4,
    tag: "Kross",
    image: "skum",
    text: "Alla minions på bordet tar 2 skada, även dina.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, allies, enemies }) => wholeTable(self, allies, enemies).forEach((player) => hitMinions(player, 2, self)),
  },
  {
    id: "salvan",
    name: "Bubbelmaskinen-salvan",
    cost: 3,
    tag: "Splash",
    image: "salva",
    text: "Alla andras minions tar 1 skada, och alla andra tar 1 klunk förbi minions.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      enemies.forEach((enemy) => {
        hitMinions(enemy, 1, self);
        addSips(enemy, 1, self);
      });
    },
  },
  {
    id: "vattenflaskan",
    name: "Vattenflaskan",
    cost: 1,
    tag: "Heal",
    image: "flaska",
    text: "Ta bort 2 klunkar.",
    effect: "heal",
    target: "self",
    play: ({ self }) => removeSips(self, 2),
  },
  {
    id: "sportdrycken",
    name: "Sportdrycken",
    cost: 3,
    tag: "Heal",
    image: "sportdryck",
    text: "Ta bort 3 klunkar, plus 1 per minion du har.",
    effect: "heal",
    target: "self",
    play: ({ self }) => removeSips(self, 3 + self.minions.filter((minion) => minion.hp > 0).length),
  },
  {
    id: "kaffe-macka",
    name: "Kaffe & macka",
    cost: 4,
    tag: "Heal",
    image: "macka",
    text: "Ta bort 5 klunkar.",
    effect: "heal",
    target: "self",
    play: ({ self }) => removeSips(self, 5),
  },
  {
    id: "frukostbuffen",
    name: "Frukostbuffén",
    cost: 6,
    tag: "Heal",
    image: "frukost",
    text: "Ta bort 8 klunkar och dra ett kort.",
    effect: "heal",
    target: "self",
    play: ({ self }) => {
      removeSips(self, 8);
      drawCard(self);
    },
  },
  {
    id: "andra-andningen",
    name: "Andra andningen",
    cost: 0,
    tag: "Comeback",
    image: "andning",
    text: "Har du minst 20 klunkar tar du bort 6. Annars händer inget.",
    effect: "heal",
    target: "self",
    play: ({ self }) => {
      if (self.sipsTaken >= 20) removeSips(self, 6);
    },
  },
  {
    id: "promenaden",
    name: "Promenaden",
    cost: 2,
    tag: "Heal",
    image: "promenad",
    text: "Ta bort 4 klunkar. Du drar inget kort i början av din nästa tur.",
    effect: "heal",
    target: "self",
    play: ({ self }) => {
      removeSips(self, 4);
      self.skipDraw = true;
    },
  },
  {
    id: "pappmuggen",
    name: "Pappmuggen",
    cost: 0,
    tag: "Minion",
    image: "pappmugg",
    text: "Placera Pappmuggen, en försvarare med 1 HP.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Pappmuggen", 1),
  },
  {
    id: "olbacken",
    name: "Ölbacken",
    cost: 3,
    tag: "Minion",
    image: "olback",
    text: "Placera Ölbacken, en försvarare med 4 HP.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Ölbacken", 4),
  },
  {
    id: "underlaggsstapeln",
    name: "Underläggsstapeln",
    cost: 3,
    tag: "Minion",
    image: "underlagg",
    text: "Placera tre Underlägg med 1 HP var.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      for (let coaster = 0; coaster < 3; coaster += 1) summonMinion(self, "Underlägg", 1);
    },
  },
  {
    id: "kylvaskan",
    name: "Kylväskan",
    cost: 4,
    tag: "Minion",
    image: "kylvaska",
    text: "Placera Kylväskan (4 HP). När den slås ut tar du bort 3 klunkar.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Kylväskan", 4, "cooler"),
  },
  {
    id: "isskulpturen",
    name: "Isskulpturen",
    cost: 4,
    tag: "Minion",
    image: "isskulptur",
    text: "Placera Isskulpturen (7 HP). Den smälter 2 HP vid varje turslut.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Isskulpturen", 7, "melt"),
  },
  {
    id: "dorrvakten",
    name: "Dörrvakten",
    cost: 5,
    tag: "Minion",
    image: "dorrvakt",
    text: "Placera Dörrvakten (5 HP). Kort som går förbi minions träffar ändå den först.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Dörrvakten", 5, "bouncer"),
  },
  {
    id: "glasunderlagget",
    name: "Glasunderlägget",
    cost: 1,
    tag: "Skydd",
    image: "glasunderlagg",
    text: "Nästa attack mot dig gör 2 klunkar mindre.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      self.armor = 2;
    },
  },
  {
    id: "djn",
    name: "DJ:n",
    cost: 4,
    tag: "Minion",
    image: "dj",
    text: "Placera DJ:n (3 HP). Vid ditt turslut tar alla andra 1 klunk.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "DJ:n", 3, "dj"),
  },
  {
    id: "shottornet",
    name: "Shottornet",
    cost: 5,
    tag: "Minion",
    image: "shottorn",
    text: "Placera Shottornet (4 HP). Vid ditt turslut: 2 klunkar till en slumpad motståndare.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Shottornet", 4, "tower"),
  },
  {
    id: "kassoren",
    name: "Kassören",
    cost: 3,
    tag: "Minion",
    image: "kassa",
    text: "Placera Kassören (2 HP). Du får 1 extra mana i början av varje tur.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Kassören", 2, "cashier"),
  },
  {
    id: "partyhatten",
    name: "Partyhatten",
    cost: 1,
    tag: "Minion",
    image: "partyhatt",
    text: "Placera Partyhatten (1 HP). Så länge den lever gör dina kort 1 klunk extra per träff.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Partyhatten", 1, "partyhat"),
  },
  {
    id: "fotografen",
    name: "Fotografen",
    cost: 2,
    tag: "Minion",
    image: "kamera",
    text: "Placera Fotografen (2 HP). När en av dina andra minions slås ut drar du ett kort.",
    effect: "shield",
    target: "self",
    play: ({ self }) => summonMinion(self, "Fotografen", 2, "photographer"),
  },
  {
    id: "fyllekompisen",
    name: "Fyllekompisen",
    cost: 3,
    tag: "Parasit",
    image: "kompis",
    text: "Sätt Fyllekompisen (2 HP) hos målet. I början av värdens tur dricker värden 1 klunk, och sen vinglar kompisen vidare till en annan motståndare.",
    effect: "trick",
    target: "enemy",
    play: ({ self, target }) => summonMinion(target, "Fyllekompisen", 2, "drifter", self),
  },
  {
    id: "barkortet",
    name: "Barkortet",
    cost: 2,
    tag: "Dra",
    image: "barkort",
    text: "Dra ett kort per motståndare som har fler kort än du.",
    effect: "trick",
    target: "self",
    play: ({ self, enemies }) => {
      const richer = enemies.filter((enemy) => enemy.hand.length > self.hand.length).length;
      for (let draw = 0; draw < richer; draw += 1) drawCard(self);
    },
  },
  {
    id: "pafyllningen",
    name: "Påfyllningen",
    cost: 4,
    tag: "Dra",
    image: "pafyll",
    text: "Dra tills du har 5 kort på handen.",
    effect: "trick",
    target: "self",
    // Counted up front rather than "while the hand is short": a deck that runs dry
    // would otherwise spin forever.
    play: ({ self }) => {
      for (let held = self.hand.length; held < 5; held += 1) drawCard(self);
    },
  },
  {
    id: "spill-pa-golvet",
    name: "Spill på golvet",
    cost: 3,
    tag: "Kortlek",
    image: "golv",
    text: "Alla motståndare slänger de 2 översta korten i sin kortlek.",
    effect: "trick",
    target: "allEnemies",
    play: ({ enemies }) => enemies.forEach((enemy) => millCards(enemy, 2)),
  },
  {
    id: "slutsalt",
    name: "Slutsålt",
    cost: 2,
    tag: "Hand",
    image: "slutsalt",
    text: "Målet slänger ett slumpat kort från handen.",
    effect: "trick",
    target: "enemy",
    play: ({ target }) => discardCard(target),
  },
  {
    id: "samma-igen",
    name: "Samma igen",
    cost: 3,
    tag: "Kopia",
    image: "igen",
    text: "Spela ditt förra kort en gång till, gratis — mot samma mål om det står kvar, annars ett slumpat.",
    effect: "trick",
    target: "self",
    play: ({ self, enemies, allies }) => {
      const last = self.lastPlayed;
      const card = last && last.cardId !== "samma-igen" ? library.find((candidate) => candidate.id === last.cardId) : null;
      if (!card) return;
      let target = null;
      if (card.target === "enemy") {
        if (!enemies.length) return;
        target = enemies.find((enemy) => enemy.id === last.targetId) || enemies[randomInt(0, enemies.length - 1)];
      }
      card.play({ self, target, enemies, allies });
    },
  },
  {
    id: "kontanter",
    name: "Kontanter",
    cost: 0,
    tag: "Mana",
    image: "kontant",
    text: "Få 1 mana.",
    effect: "trick",
    target: "self",
    play: ({ self }) => {
      self.mana += 1;
    },
  },
  {
    id: "dricksburken",
    name: "Dricksburken",
    cost: 2,
    tag: "Mana",
    image: "burk",
    text: "Få 1 mana per minion du har.",
    effect: "trick",
    target: "self",
    play: ({ self }) => {
      self.mana += self.minions.filter((minion) => minion.hp > 0).length;
    },
  },
  {
    id: "notan",
    name: "Notan",
    cost: 3,
    tag: "Tempo",
    image: "kvitto",
    text: "Målet får 2 mindre mana nästa tur.",
    effect: "trick",
    target: "enemy",
    play: ({ target }) => {
      target.manaPenalty += 2;
    },
  },
  {
    id: "fri-bar",
    name: "Fri bar",
    cost: 5,
    tag: "Rabatt",
    image: "fribar",
    text: "Dina två nästa kort kostar 2 mindre var.",
    effect: "trick",
    target: "self",
    play: ({ self }) => {
      self.freeBar = 2;
    },
  },
  {
    id: "kontraskalen",
    name: "Kontraskålen",
    cost: 3,
    tag: "Reaktion",
    image: "kontra",
    text: "Nästa gång du dricker drar du lika många kort, högst 2.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      self.counterDraw = true;
    },
  },
  {
    id: "tvavagsspegeln",
    name: "Tvåvägsspegeln",
    cost: 5,
    tag: "Reflect",
    image: "tvavag",
    text: "Nästa attack mot dig går vidare till en slumpad annan motståndare.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      self.rebound = true;
    },
  },
  {
    id: "livlinan",
    name: "Livlinan",
    cost: 2,
    tag: "Reaktion",
    image: "livlina",
    text: "Första gången du når 24 klunkar tar du direkt bort 5.",
    effect: "shield",
    target: "self",
    play: ({ self }) => {
      self.lifeline = true;
    },
  },
  {
    id: "tarningen",
    name: "Tärningen",
    cost: 1,
    tag: "Chans",
    image: "tarning",
    text: "Slå en tärning. På 1–2 tar du 3 klunkar, annars tar målet så många som tärningen visar.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      const roll = randomInt(1, 6);
      if (roll <= 2) addSips(self, 3, self);
      else attackPlayer(self, target, roll);
    },
  },
  {
    id: "myntkastet",
    name: "Myntkastet",
    cost: 2,
    tag: "Chans",
    image: "mynt",
    text: "Krona: målet tar 6 klunkar. Klave: du tar 2.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      if (randomInt(0, 1) === 0) attackPlayer(self, target, 6);
      else addSips(self, 2, self);
    },
  },
  {
    id: "stolleken",
    name: "Stolleken",
    cost: 4,
    tag: "Byte",
    image: "stolar",
    text: "Alla händer flyttar ett steg runt bordet.",
    effect: "trick",
    target: "self",
    // Hands only change owner, never size, so nobody can end up over the cap.
    play: ({ self, allies, enemies }) => {
      const table = wholeTable(self, allies, enemies);
      const hands = table.map((player) => player.hand);
      table.forEach((player, seat) => {
        player.hand = hands[(seat + hands.length - 1) % hands.length];
      });
    },
  },
  {
    id: "lyckohjulet",
    name: "Lyckohjulet",
    cost: 3,
    tag: "Chans",
    image: "hjul",
    text: "Snurra: 3 klunkar på en slumpad motståndare, ta bort 3, dra 2 kort, få 2 mana eller placera Lyckokatten (3 HP).",
    effect: "trick",
    target: "self",
    play: ({ self, enemies }) => {
      const spin = randomInt(1, 5);
      if (spin === 1 && enemies.length) attackPlayer(self, enemies[randomInt(0, enemies.length - 1)], 3);
      else if (spin === 2) removeSips(self, 3);
      else if (spin === 3) {
        drawCard(self);
        drawCard(self);
      } else if (spin === 4) self.mana += 2;
      else if (spin === 5) summonMinion(self, "Lyckokatten", 3);
    },
  },
  {
    id: "sista-skalen",
    name: "Sista skålen",
    cost: 5,
    tag: "Comeback",
    image: "sistaskal",
    text: "Har du druckit mest vid bordet tar alla andra 4 klunkar.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, allies, enemies }) => {
      const others = wholeTable(self, allies, enemies).slice(1);
      if (!others.every((player) => self.sipsTaken > player.sipsTaken)) return;
      enemies.forEach((enemy) => attackPlayer(self, enemy, 4));
    },
  },
  // The second wave's teamwork cards. Buildable like the rest, under the builder's Lag tab.
  {
    id: "high-five",
    name: "High five",
    cost: 1,
    tag: "Lagarbete",
    image: "highfive",
    text: "Du och din partner drar ett kort var.",
    effect: "trick",
    target: "self",
    teamOnly: true,
    play: ({ self, allies = [] }) => {
      drawCard(self);
      allies.forEach((mate) => drawCard(mate));
    },
  },
  {
    id: "ryggdunken",
    name: "Ryggdunken",
    cost: 2,
    tag: "Lagarbete",
    image: "ryggdunk",
    text: "Flytta upp till 4 klunkar från din partner till dig.",
    effect: "heal",
    target: "self",
    teamOnly: true,
    play: ({ self, allies = [] }) => {
      const [mate] = allies;
      const moved = mate ? Math.min(4, mate.sipsTaken) : 0;
      if (!moved) return;
      removeSips(mate, moved);
      // Sourceless and past your own minions: you chose to take them.
      addSips(self, moved);
    },
  },
  {
    id: "tagteam",
    name: "Tagteam",
    cost: 3,
    tag: "Lagarbete",
    image: "tagteam",
    text: "Din partner får 2 extra mana nästa tur.",
    effect: "trick",
    target: "self",
    teamOnly: true,
    // A negative penalty is a bonus: the turn start takes maxMana minus the penalty.
    play: ({ allies = [] }) => allies.forEach((mate) => {
      mate.manaPenalty -= 2;
    }),
  },
  {
    id: "lagkaptenen",
    name: "Lagkaptenen",
    cost: 4,
    tag: "Lagarbete",
    image: "kapten",
    text: "Sätt Lagkaptenen (3 HP) hos din partner. Vid partnerns turslut tar ni båda bort 1 klunk.",
    effect: "shield",
    target: "self",
    teamOnly: true,
    play: ({ self, allies = [] }) => allies.forEach((mate) => summonMinion(mate, "Lagkaptenen", 3, "captain", self)),
  },
  // Two late chance cards, built for the suspense show: each has its own moment in
  // public/js/game/gambleShow.js, which reads back the rolls these make.
  {
    id: "snurra-flaskan",
    name: "Snurra flaskan",
    cost: 2,
    tag: "Chans",
    image: "snurr",
    text: "Flaskan pekar ut en slumpad spelare — kanske du — som tar 4 klunkar. Pekar den på dig tar du 2 men drar 2 kort.",
    effect: "damage",
    target: "allEnemies",
    play: ({ self, enemies }) => {
      const table = [self, ...enemies];
      const pick = table[randomInt(0, table.length - 1)];
      if (pick !== self) {
        attackPlayer(self, pick, 4);
        return;
      }
      // Like Skål!: your own glass goes straight down, past your own minions.
      addSips(self, 2, self);
      drawCard(self);
      drawCard(self);
    },
  },
  {
    id: "tarningsduellen",
    name: "Tärningsduellen",
    cost: 2,
    tag: "Chans",
    image: "duell",
    text: "Du och målet slår var sin tärning. Lägst slag tar skillnaden gånger två. Lika: målet tar 2.",
    effect: "damage",
    target: "enemy",
    play: ({ self, target }) => {
      const mine = randomInt(1, 6);
      const theirs = randomInt(1, 6);
      if (mine === theirs) attackPlayer(self, target, 2);
      else if (mine > theirs) attackPlayer(self, target, (mine - theirs) * 2);
      else addSips(self, (theirs - mine) * 2, self);
    },
  },
  // The mini-game cards: the card only starts the game; the controller runs it on every
  // phone at the table and settles it (public/js/game/miniGames.js has the rules).
  {
    id: "blackjack",
    name: "Blackjack",
    cost: 2,
    tag: "Minispel",
    image: "blackjack",
    text: "Alla spelar Blackjack på telefonen. Närmast 21 vinner — lika blir omspel — och vinnaren delar ut 8 klunkar.",
    effect: "trick",
    target: "self",
    play: ({ self }) => startMinigame("blackjack", self),
  },
  {
    id: "utrostningen",
    name: "Utröstningen",
    cost: 2,
    tag: "Minispel",
    image: "rosta",
    text: "Alla röstar ut någon på telefonen. Varje röst blir en lott — den lotten landar på tar 5 klunkar.",
    effect: "trick",
    target: "self",
    play: ({ self }) => startMinigame("vote", self),
  },
  ];
  return library;
}
